import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Modal from 'components/Luxkit/Modal/Modal'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import { rem } from 'polished'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import CarHireImportantDetailsContent from './CarHireImportantDetailsContent'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import { fillArray, unique } from 'lib/array/arrayUtils'
import useCarHireOfferImportantInformation from 'hooks/CarHire/useCarHireImportantInformation'
import { connect } from 'react-redux'
import { isCarHireInsuranceAvailable } from 'selectors/carHireSelectors'

const TabContent = styled(VerticalSpacer)`
  min-height: ${rem(275)};
`
const LoadingWrapper = styled(Group)`
  min-height: ${rem(275)};
`

interface Props {
  offer: App.CarHireOffer;
  offerImportantInformationLists?: Record<string, App.CarHireOfferImportantInformationList>;
  pickUpDate: string;
  dropOffDate: string;
  pickUpTime: string;
  dropOffTime: string;
  showInclusions: boolean;
  showCoverage: boolean;
  title: string;
  openTab?: App.CarHireImportantInformationCategory
}

export enum TabOptions {
  INCLUSIONS = 'Inclusions',
  COVERAGE = 'Coverage',
  DRIVER_AND_VEHICLE = 'Driver & Vehicle',
  DEPOSIT_AND_PAYMENT = 'Deposit & Payment',
  TRAVEL_RESTRICTIONS = 'Travel Restrictions',
  OTHER = 'Other'
}

function CarHireInclusionsAndDetailsModal(props: Props) {
  const {
    offer,
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
    showInclusions,
    showCoverage,
    title,
    openTab,
  } = props
  const { inclusions, fuelPolicy } = offer.copy

  const rateOption = offer.rateOptions[0]

  const params: App.CarHireOfferImportantInformationParams = {
    pickUpLocation: {
      code: offer.pickUp.id,
      date: pickUpDate || '',
      time: pickUpTime || '',
    },
    dropOffLocation: {
      code: offer.dropOff.id,
      date: dropOffDate || '',
      time: dropOffTime || '',
    },
    reference: {
      id: rateOption.id,
      idContext: rateOption.idContext,
      type: rateOption.type,
      dateTime: rateOption.dateTime,
      url: rateOption.url,
    },
  }

  const { offerImportantInformation } = useCarHireOfferImportantInformation(params, rateOption.id)
  const isFetching = offerImportantInformation.fetching
  const hasError = Boolean(offerImportantInformation.error)
  const offerImportantInformationData = offerImportantInformation.data

  const tabOptions: Array<App.CarHireImportantInformationCategory> = useMemo(() => {
    const tabs = Array.from(new Set((offerImportantInformationData || [])
      .filter(item => {
        return item.paragraphs.length > 0 && (item.category !== TabOptions.COVERAGE || showCoverage)
      })
      .map(item => item.category)))

    if (fuelPolicy) {
      tabs.unshift(TabOptions.DRIVER_AND_VEHICLE)
    }

    if (tabs.includes(TabOptions.COVERAGE)) {
      tabs.unshift(TabOptions.COVERAGE)
    }

    if (inclusions.length > 0 && showInclusions) {
      tabs.unshift(TabOptions.INCLUSIONS)
    }

    const uniqueTabs = unique(tabs)

    return uniqueTabs
  }, [fuelPolicy, inclusions, offerImportantInformationData, showCoverage, showInclusions])

  useEffect(() => {
    if (openTab && tabOptions?.includes(openTab)) {
      setActiveTab(openTab)
    }
  }, [openTab, tabOptions])

  const [activeTab, setActiveTab] = useState<App.CarHireImportantInformationCategory>(tabOptions[0])

  useEffect(() => {
    if (!activeTab) setActiveTab(tabOptions[0])
  }, [activeTab, tabOptions])

  return (
    <Modal title={title} size="L">
      <TabButtonsGroup>
        {tabOptions.map((option) => (
          <TabButton
            isActive={option === activeTab}
            key={option}
            size="medium"
            onClick={() => setActiveTab(option)}
          >
            {option}
          </TabButton>
        ))}

        {offerImportantInformation.fetching && !hasError &&
          <>
            {fillArray(3).map((id) => (
              <TabButton
                key={id}
                isActive={false}
                size="medium"
              >
                <LoadingSpinner />
              </TabButton>
            ))}
          </>
        }
      </TabButtonsGroup>
      <TabContent gap={4}>
        <Group direction="vertical" gap={16}>
          {isFetching && !hasError && !activeTab &&
            <LoadingWrapper direction="horizontal" fullHeight horizontalAlign="center" verticalAlign="center">
              <LoadingSpinner size={48} />
            </LoadingWrapper>
          }

          <CarHireImportantDetailsContent
            offerImportantInformation={offerImportantInformationData}
            activeTab={activeTab}
            offer={offer}
            hasError={hasError}
            isFetching={isFetching}
          />
        </Group>
      </TabContent>
    </Modal>
  )
}

const mapStateToProps = (state: App.State) => ({
  showCoverage: !isCarHireInsuranceAvailable(state),
})

export default connect(mapStateToProps)(CarHireInclusionsAndDetailsModal)
